import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { programs, sites } from '@cwos/api-client';
import type { sites as SitesNS } from '@cwos/api-client';
import {
  FilteredEmpty,
  Pill,
  SiteMapPicker,
  SkeletonSet,
  SuccessEmpty,
  TableCard,
  type Column,
} from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsField, SettingsHeader } from './common.js';
import {
  EMPTY_SITE_DRAFT,
  emptyReason,
  geofenceLabel,
  hasNextPage,
  hasPreviousPage,
  rangeLabel,
  siteCreatePayload,
  statusBadge,
  validateSiteDraft,
  type SiteDraft,
} from './organizationLogic.js';
import { LoadFailure } from '../LoadFailure.js';

const PAGE_SIZE = 25;
const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

/**
 * Sites & locations — the places your organization operates from.
 *
 * A Site here is always a business operational location: an office, clinic,
 * community or partner site, field hub. It is never an infrastructure region,
 * and it is never somewhere a client lives — client addresses are a different,
 * far more sensitive kind of record and have no home on this screen.
 *
 * The create flow is address-first: type an address, find it on the map, adjust
 * the pin if it landed badly, then optionally switch on a geofence. If the
 * lookup fails, the site can still be saved without a position and fixed later —
 * nothing is invented to fill the gap.
 */
export function SitesSettings() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const navigate = useNav();
  const canManage = has('location:manage');

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'default' | 'active' | 'inactive' | 'retired' | 'all'>(
    'default',
  );
  const [offset, setOffset] = useState(0);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<SiteDraft>(EMPTY_SITE_DRAFT);
  const [createError, setCreateError] = useState<string | null>(null);

  const listQuery = {
    q: query.trim() || undefined,
    status: status === 'default' ? undefined : status,
    limit: PAGE_SIZE,
    offset,
  };

  const list = useQuery({
    queryKey: ['sites', 'list', listQuery],
    queryFn: () => sites.list(listQuery),
  });

  const programOptions = useQuery({
    queryKey: ['programs', 'options'],
    queryFn: () => programs.list({ limit: 200, sort: 'name' }),
    enabled: creating && canManage,
  });

  const create = useMutation({
    mutationFn: () => sites.create(siteCreatePayload(draft)),
    onSuccess: (site) => {
      setCreating(false);
      setDraft(EMPTY_SITE_DRAFT);
      setCreateError(null);
      void qc.invalidateQueries({ queryKey: ['sites'] });
      void qc.invalidateQueries({ queryKey: ['programs'] });
      navigate(`/settings/sites/${site.id}`);
    },
    onError: (err: unknown) =>
      setCreateError(err instanceof Error ? err.message : 'We couldn’t add that site.'),
  });

  const errors = validateSiteDraft(draft);
  const canSubmit = Object.keys(errors).length === 0 && draft.name.trim().length > 0;

  const columns: Column<SitesNS.Site>[] = [
    {
      key: 'name',
      header: 'Site',
      render: (s) => (
        <span>
          <span style={{ fontWeight: 600 }}>{s.name}</span>
          {s.code ? <span className="rn-muted"> · {s.code}</span> : null}
        </span>
      ),
    },
    { key: 'address', header: 'Address', render: (s) => s.addressSummary ?? '—' },
    {
      key: 'status',
      header: 'Status',
      render: (s) => {
        const badge = statusBadge(s.status);
        return (
          <Pill variant={badge.variant} dot>
            {badge.label}
          </Pill>
        );
      },
    },
    { key: 'geofence', header: 'Geofence', render: (s) => geofenceLabel(s) },
    { key: 'programs', header: 'Programs', numeric: true, render: (s) => String(s.programCount) },
  ];

  const reason = emptyReason(query, status);

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title="Sites & locations"
        blurb="The physical places your organization works from. Sites give schedules, time clock and the workforce map a shared idea of “where”."
      />

      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">All sites</h2>
          {canManage ? (
            <button
              type="button"
              className="rn-btn-quiet"
              aria-expanded={creating}
              onClick={() => {
                setCreating((c) => !c);
                setCreateError(null);
              }}
            >
              {creating ? 'Cancel' : 'Add site'}
            </button>
          ) : null}
        </div>

        {creating && canManage ? (
          <form
            className="org-create"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) create.mutate();
            }}
          >
            <div className="org-create-grid">
              <SettingsField label="Name" error={errors['name']}>
                <input
                  style={{ marginBottom: 0 }}
                  value={draft.name}
                  autoFocus
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Short code (optional)" error={errors['code']}>
                <input
                  style={{ marginBottom: 0 }}
                  value={draft.code}
                  onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                />
              </SettingsField>
            </div>

            <fieldset className="org-fieldset">
              <legend className="rn-muted">Address</legend>
              <div className="org-create-grid">
                <SettingsField label="Street">
                  <input
                    style={{ marginBottom: 0 }}
                    value={draft.addressLine1}
                    onChange={(e) => setDraft({ ...draft, addressLine1: e.target.value })}
                  />
                </SettingsField>
                <SettingsField label="Suite / floor">
                  <input
                    style={{ marginBottom: 0 }}
                    value={draft.addressLine2}
                    onChange={(e) => setDraft({ ...draft, addressLine2: e.target.value })}
                  />
                </SettingsField>
                <SettingsField label="City">
                  <input
                    style={{ marginBottom: 0 }}
                    value={draft.city}
                    onChange={(e) => setDraft({ ...draft, city: e.target.value })}
                  />
                </SettingsField>
                <SettingsField label="State / region">
                  <input
                    style={{ marginBottom: 0 }}
                    value={draft.region}
                    onChange={(e) => setDraft({ ...draft, region: e.target.value })}
                  />
                </SettingsField>
                <SettingsField label="Postal code">
                  <input
                    style={{ marginBottom: 0 }}
                    value={draft.postalCode}
                    onChange={(e) => setDraft({ ...draft, postalCode: e.target.value })}
                  />
                </SettingsField>
                <SettingsField label="Country">
                  <input
                    style={{ marginBottom: 0 }}
                    value={draft.country}
                    onChange={(e) => setDraft({ ...draft, country: e.target.value })}
                  />
                </SettingsField>
              </div>
            </fieldset>

            <SiteMapPicker
              address={draft}
              latitude={draft.latitude}
              longitude={draft.longitude}
              coordinateSource={draft.coordinateSource}
              coordinateSetAt={null}
              geofenceRadiusMeters={draft.geofenceEnabled ? draft.geofenceRadiusMeters : null}
              mapsApiKey={MAPS_KEY}
              onChange={(next) =>
                setDraft({
                  ...draft,
                  latitude: next.latitude,
                  longitude: next.longitude,
                  coordinateSource: next.source,
                  resolvedAddress: next.resolvedAddress ?? null,
                })
              }
              onClear={() =>
                setDraft({
                  ...draft,
                  latitude: null,
                  longitude: null,
                  coordinateSource: 'unknown',
                  resolvedAddress: null,
                  geofenceEnabled: false,
                })
              }
            />

            <fieldset className="org-fieldset">
              <legend className="rn-muted">Geofence</legend>
              <label className="org-check">
                <input
                  type="checkbox"
                  checked={draft.geofenceEnabled}
                  onChange={(e) => setDraft({ ...draft, geofenceEnabled: e.target.checked })}
                />
                <span>
                  Check clock-ins against this site
                  <span className="rn-muted">
                    {' '}
                    — this creates a real work geofence that your location policy evaluates.
                  </span>
                </span>
              </label>
              {draft.geofenceEnabled ? (
                <SettingsField
                  label="Radius (metres)"
                  hint="Between 1 m and 50 km. A geofence marks a site, not a region."
                  error={errors['geofence']}
                >
                  <input
                    style={{ marginBottom: 0, maxWidth: 180 }}
                    inputMode="numeric"
                    value={String(draft.geofenceRadiusMeters)}
                    onChange={(e) =>
                      setDraft({ ...draft, geofenceRadiusMeters: Number(e.target.value) || 0 })
                    }
                  />
                </SettingsField>
              ) : null}
              {errors['geofence'] && !draft.geofenceEnabled ? null : null}
            </fieldset>

            <fieldset className="org-fieldset">
              <legend className="rn-muted">Programs operating here (optional)</legend>
              {programOptions.isLoading ? (
                <SkeletonSet surface="table" rows={2} />
              ) : programOptions.isError && programOptions.data === undefined ? (
                <p className="rn-muted" style={{ fontSize: 13 }} role="status">
                  We couldn’t load your programs, so none are offered here. You can attach them
                  later.
                </p>
              ) : (programOptions.data?.programs.length ?? 0) === 0 ? (
                <p className="rn-muted" style={{ fontSize: 13 }}>
                  No programs yet — you can attach them later.
                </p>
              ) : (
                <div className="org-checklist">
                  {(programOptions.data?.programs ?? []).map((program) => (
                    <label key={program.id} className="org-check">
                      <input
                        type="checkbox"
                        checked={draft.programIds.includes(program.id)}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            programIds: e.target.checked
                              ? [...draft.programIds, program.id]
                              : draft.programIds.filter((id) => id !== program.id),
                          })
                        }
                      />
                      <span>{program.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </fieldset>

            {createError ? (
              <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
                {createError}
              </p>
            ) : null}

            <div className="set-actions">
              <button
                type="submit"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={!canSubmit || create.isPending}
              >
                {create.isPending ? 'Adding…' : 'Add site'}
              </button>
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => {
                  setCreating(false);
                  setDraft(EMPTY_SITE_DRAFT);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        <div className="org-filters" role="search">
          <label className="org-filter">
            <span className="rn-muted">Search</span>
            <input
              style={{ marginBottom: 0 }}
              type="search"
              placeholder="Name, code, city or street"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOffset(0);
              }}
            />
          </label>
          <label className="org-filter">
            <span className="rn-muted">Status</span>
            <select
              style={{ marginBottom: 0 }}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as typeof status);
                setOffset(0);
              }}
            >
              <option value="default">Active and inactive</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
              <option value="retired">Retired only</option>
              <option value="all">Everything</option>
            </select>
          </label>
        </div>

        {list.isLoading ? (
          <SkeletonSet surface="table" rows={5} />
        ) : list.isError && !list.data ? (
          <LoadFailure
            error={list.error}
            message="We couldn’t load sites just now."
            onRetry={() => void list.refetch()}
          />
        ) : list.data && list.data.sites.length === 0 ? (
          reason === 'no_matches' ? (
            <FilteredEmpty
              onClear={() => {
                setQuery('');
                setStatus('default');
                setOffset(0);
              }}
            />
          ) : (
            <SuccessEmpty
              heading="No sites configured"
              message="Add a site if your organization works from physical locations. Field and community teams don’t need one — schedules and time clock work without it."
            />
          )
        ) : (
          <>
            <TableCard
              columns={columns}
              rows={list.data?.sites ?? []}
              rowKey={(s) => s.id}
              onRowOpen={(s) => navigate(`/settings/sites/${s.id}`)}
              caption="Sites in this organization"
            />
            <div className="org-pager">
              <span className="rn-muted">
                {rangeLabel(list.data?.total ?? 0, PAGE_SIZE, offset, list.data?.sites.length ?? 0)}
              </span>
              <button
                type="button"
                className="rn-btn-quiet"
                disabled={!hasPreviousPage(offset)}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </button>
              <button
                type="button"
                className="rn-btn-quiet"
                disabled={!hasNextPage(list.data?.total ?? 0, PAGE_SIZE, offset)}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </SettingsCard>
    </div>
  );
}
